/**
 * @format
 */
import { Action, createReducer, on } from "@ngrx/store";
import { PreviewEditTemplateActions } from "./preview-edit-template.actions";
import { PreviewTemplateContentResponse, TemplateParameterItemType } from "./preview-edit-template.model";

export const STORE_FEATURE_KEY_PREVIEW_TEMPLATE = "previewTemplate";

export interface TemplateUpdateModel {
   updatedModel: any;
   isModelUpdated: boolean;
   updateType: string;
   type: string;
}

export interface ReloadPreviewTemplate {
   templateId: number;
   userId: number;
   templateFileName: string;
   isConfirm: boolean;
}
export interface PreviewTemplateState {
   loading: boolean;
   previewEditTemplate: any;
   contextVariables: any;
   contentResponse: PreviewTemplateContentResponse;
   sourceLoading: boolean;
   validatedSource: boolean;
   savedCustomTemplate: boolean;
   templateLastModified: any;
   loadPreviewContent: boolean;
   loadingMessageContentByTemplateId: boolean;
   templateParameters: Array<TemplateParameterItemType>;
   locations: Array<any>;
   locationEntities: Array<any>;
   providers: Array<any>;
   isModelProcessed: boolean;
   mediaDevices: Array<any>;
   errorMessage: string;
   templateUpdateModel: TemplateUpdateModel;
   totalStrategies: number;
   isMediaProcessed: boolean;
   reloadPreviewTemplate: Partial<ReloadPreviewTemplate>;
   templateState: any;
   isSaving: boolean;
   smsParts: number;
   smsParsedTextLength: number;
   emailPreventChanges: boolean;
   messagePreventChanges: boolean;
}

export const initializePreviewTemplateState: PreviewTemplateState = {
   loading: false,
   previewEditTemplate: {},
   reloadPreviewTemplate: {},
   contextVariables: [],
   contentResponse: undefined,
   sourceLoading: false,
   validatedSource: false,
   savedCustomTemplate: false,
   templateLastModified: undefined,
   loadPreviewContent: false,
   loadingMessageContentByTemplateId: false,
   templateParameters: [],
   locations: [],
   locationEntities: [],
   providers: [],
   isModelProcessed: false,
   mediaDevices: [],
   errorMessage: undefined,
   templateUpdateModel: {
      updatedModel: {},
      isModelUpdated: false,
      updateType: undefined,
      type: undefined,
   },
   totalStrategies: -1,
   isMediaProcessed: false,
   templateState: undefined,
   isSaving: false,
   smsParts: 1,
   smsParsedTextLength: 1,
   emailPreventChanges: false,
   messagePreventChanges: false,
};

const reducer = createReducer(
   initializePreviewTemplateState,
   on(PreviewEditTemplateActions.getModel, (state, payload) => ({
      ...state,
      previewEditTemplate: {},
      templateState: undefined,
      loading: true,
   })),
   on(PreviewEditTemplateActions.getModelSuccess, (state, { previewEditTemplate }) => ({
      ...state,
      previewEditTemplate,
      loading: false,
      errorMessage: undefined,
   })),
   on(PreviewEditTemplateActions.getModelFail, (state, { previewEditTemplate }) => ({ ...state, previewEditTemplate })),
   on(PreviewEditTemplateActions.displayDialogOnError, (state, { error }) => ({
      ...state,
      loading: false,
      loadingMessageContentByTemplateId: false,
      errorMessage: error,
   })),
   on(PreviewEditTemplateActions.preprocessModel, (state, payload) => ({
      ...state,
      isModelProcessed: false,
   })),
   on(PreviewEditTemplateActions.preprocessModelSuccess, (state, { contextVariables }) => ({
      ...state,
      contextVariables,
      isModelProcessed: true,
      errorMessage: undefined,
   })),
   on(PreviewEditTemplateActions.preprocessModelFail, (state, payload) => ({
      ...state,
      isModelProcessed: false,
   })),
   on(PreviewEditTemplateActions.processPreviewTemplate, (state, payload) => ({
      ...state,
      contentResponse: undefined,
      loadingMessageContentByTemplateId: true,
      errorMessage: undefined,
      templateUpdateModel: {
         ...state.templateUpdateModel,
         updatedModel: payload.model,
      },
      reloadPreviewTemplate: {
         templateId: payload.templateId,
         userId: payload.userId,
         templateFileName: payload.templateFilename,
         isConfirm: payload.isConfirm,
      },
   })),
   on(PreviewEditTemplateActions.processPreviewTemplateSuccess, (state, { contentResponse }) => ({
      ...state,
      contentResponse,
      loadPreviewContent: true,
      loadingMessageContentByTemplateId: false,
   })),
   on(PreviewEditTemplateActions.processPreviewTemplateFail, (state, payload) => ({
      ...state,
      contentResponse: { mailContent: undefined, voiceContent: undefined },
      loading: false,
      loadingMessageContentByTemplateId: false,
   })),
   on(PreviewEditTemplateActions.saveSource, (state, payload) => ({
      ...state,
      sourceLoading: true,
      templateLastModified: undefined,
   })),
   on(PreviewEditTemplateActions.saveSourceSuccess, (state, { lastModified }) => ({
      ...state,
      sourceLoading: false,
      validatedSource: true,
      templateLastModified: lastModified,
      errorMessage: undefined,
   })),
   on(PreviewEditTemplateActions.validateSource, (state, payload) => ({
      ...state,
      validatedSource: false,
   })),
   on(PreviewEditTemplateActions.validateSourceSuccess, (state, payload) => ({
      ...state,
      validatedSource: true,
      errorMessage: undefined,
   })),
   on(PreviewEditTemplateActions.openPreviewEditTemplateDialog, (state, payload) => ({
      ...state,
      previewEditTemplate: {},
      savedCustomTemplate: false,
      loadPreviewContent: false,
   })),
   on(PreviewEditTemplateActions.openPreviewEditTemplateDialogFromAngularJSSuccess, (state, payload) => ({
      ...state,
      savedCustomTemplate: true,
      loadPreviewContent: false,
      errorMessage: undefined,
      totalStrategies: -1,
   })),
   on(PreviewEditTemplateActions.saveModel, (state, payload) => ({
      ...state,
      templateLastModified: undefined,
   })),
   on(PreviewEditTemplateActions.saveModelSuccess, (state, { lastModified }) => ({
      ...state,
      templateLastModified: lastModified,
      isModelUpdated: false,
      errorMessage: undefined,
   })),
   on(PreviewEditTemplateActions.getTemplateParametersSuccess, (state, { templateParameters }) => ({
      ...state,
      templateParameters,
   })),
   on(PreviewEditTemplateActions.getLocationsSuccess, (state, { locations }) => ({ ...state, locations })),
   on(PreviewEditTemplateActions.getLocationEntitiesSuccess, (state, { locationEntities }) => ({ ...state, locationEntities })),
   on(PreviewEditTemplateActions.getProvidersSuccess, (state, { providers }) => ({ ...state, providers })),
   on(PreviewEditTemplateActions.templateUpdateModel, (state, { templateUpdateModel }) => ({
      ...state,
      templateUpdateModel,
   })),
   on(PreviewEditTemplateActions.templateUpdateModelVariable, (state, { variableName, value, reloadPreview }) => ({
      ...state,
      templateUpdateModel: {
         ...state.templateUpdateModel,
         isModelUpdated: true,
         updateType: "imageUpload",
         updatedModel: {
            ...state.templateUpdateModel.updatedModel,
            [variableName]: value,
         },
         type: "image",
      },
      emailPreventChanges: true,
   })),
   on(PreviewEditTemplateActions.updateMedia, (state, { mediaDevices }) => ({
      ...state,
      mediaDevices,
   })),
   on(PreviewEditTemplateActions.getCountOfStrategiesMappedWithTemplateSuccess, (state, { totalStrategies }) => ({
      ...state,
      totalStrategies,
   })),
   on(PreviewEditTemplateActions.saveMediaSuccess, (state, payload) => ({
      ...state,
      isMediaProcessed: false,
   })),
   on(PreviewEditTemplateActions.saveMediaSuccess, (state, payload) => ({
      ...state,
      isMediaProcessed: true,
   })),
   on(PreviewEditTemplateActions.useImageUploaded, (state, { imagesList }) => ({
      ...state,
   })),
   on(PreviewEditTemplateActions.isTemplateUsed, (state, payload) => ({
      ...state,
      templateState: undefined,
      names: undefined,
   })),
   on(PreviewEditTemplateActions.isTemplateUsedSuccess, (state, { templateState }) => ({
      ...state,
      templateState,
   })),
   on(PreviewEditTemplateActions.setSavingStatus, (state, { savingStatus }) => ({
      ...state,
      isSaving: savingStatus,
   })),
   on(PreviewEditTemplateActions.setSmsPartsAndParsedTextLength, (state, { smsParts, smsParsedTextLength }) => ({
      ...state,
      smsParts,
      smsParsedTextLength,
   })),
   on(PreviewEditTemplateActions.setEmailPreventChanges, (state, { preventChanges }) => ({
      ...state,
      emailPreventChanges: preventChanges,
   })),
   on(PreviewEditTemplateActions.setMessagePreventChanges, (state, { preventChanges }) => ({
      ...state,
      messagePreventChanges: preventChanges,
   }))
);

export function PreviewTemplatesReducers(
   state: PreviewTemplateState | undefined,
   action: Action
): PreviewTemplateState {
   return reducer(state, action);
}
